import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import axios from "axios";
import { Col } from "antd";
import { useTranslation } from "react-i18next";
import { DownloadOutlined } from "@ant-design/icons";
import { getCredentials } from "utils/auth";
import screenSize from "utils/screenSize";
import { GraduationCert, ParticipationCerts } from "types";
import GET_LANGUAGES from "graphql/queries/GetLanguages";
import { CERT_FILENAME } from "utils/constants";
import * as Styled from "./styles";

type CertificateProps = {
  cert: ParticipationCerts | GraduationCert;
};

const Certificate: React.FC<CertificateProps> = ({ cert }) => {
  const [getLinkLoading, setLinkLoading] = useState(false);
  const { t } = useTranslation("my_certificates");
  const { data: languagesData, loading: languagesLoading } =
    useQuery(GET_LANGUAGES);
  const handleDownload = (downloadLink: string, fileName: string) => {
    setLinkLoading(true);

    const credentials: any = getCredentials();
    axios
      .get(downloadLink, {
        responseType: "blob",
        headers: credentials,
      })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();

        setLinkLoading(false);
      })
      .catch(() => {
        setLinkLoading(false);
      });
  };

  const getLanguageName = (languageLevel: string) => {
    if (!languagesLoading) {
      let i;
      for (i = 0; i < languagesData.languages.length; i += 1) {
        let j;
        const levelList = languagesData.languages[i].levels;
        for (j = 0; j < levelList?.length; j += 1) {
          if (levelList[j]?.title === languageLevel) {
            return languagesData.languages[i]?.englishName;
          }
        }
      }
    }
    return "";
  };

  const renderParticipationDetails = (certification: ParticipationCerts) => {
    if (!certification.classesCount) {
      return "";
    }
    return `${certification.classesCount} classes`;
  };

  const renderGraduationDetails = (certification: GraduationCert) => {
    if (!certification.progressPercentage) {
      return "";
    }
    return `${certification.progressPercentage}%`;
  };

  return (
    <Styled.Row key={cert.level.id}>
      <Col span={12}>
        {`${getLanguageName(cert.level.title)}: ${cert.level.title}`}
      </Col>
      <Col span={6} style={{ textAlign: "right" }}>
        {renderParticipationDetails(cert as ParticipationCerts)}
        {renderGraduationDetails(cert as GraduationCert)}
      </Col>
      <Col span={6} style={{ textAlign: "right" }}>
        <Styled.Button
          type={screenSize() === "desktop" ? "link" : "default"}
          disabled={!cert.downloadLink}
          icon={<DownloadOutlined />}
          loading={getLinkLoading}
          onClick={() => {
            handleDownload(cert.downloadLink, CERT_FILENAME.PARTICIPATION);
          }}
        >
          {screenSize() === "desktop" && t("DOWNLOAD")}
        </Styled.Button>
      </Col>
    </Styled.Row>
  );
};

export default Certificate;
